import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import { getIsLogin } from '@/utils/cookie';
import request from '@/utils/request';
import { loginUrl } from '@/service/util';
import { onLogin } from '@/utils/cookie';
import gtm from '@/utils/gtm';
import { authSignIn } from '@/service';

// interface GoogleLoginProps {
//     onSuccess?: (credential: string) => void;
//     onError?: () => void;
// }

const WEB_HOST = process.env.NEXT_PUBLIC_WEB_HOST;

export default function GoogleOneClickLogin() {
    const [isLogin, setIsLogin] = useState(() => getIsLogin());
    const router = useRouter();
    const initializationRef = useRef(false);

    const HOME_HOST = `${WEB_HOST}/${router.locale}`;

    const linkHandle = useCallback((url: string) => {
        const regex = /^(http|https|ftp):\/\//;
        if (!regex.test(url)) {
            return `${WEB_HOST}${url}`;
        }
        return url;
    }, []);

    // 前置条件：不是已登录状态，且是首页，且不是cn站
    const shouldShowComponent = useCallback(() => !isLogin && router.pathname === '/' && router.locale !== 'cn', [router.pathname, router.locale, isLogin]);

    const syncLoginShopify = useCallback(async (): Promise<string> => {
        const returnToNew = `${WEB_HOST}/${router.locale}`;
        const params = {
            returnToNew,
            url: `https://${router?.locale}.ecoflow.com`,
        };
        const res = await authSignIn(params, router?.locale);
        if (res?.code !== '0') {
            throw new Error(res?.message);
        }
        return res?.data || `/${router.locale}`;
    }, [router.locale]);

    // 处理Google一键登录成功后的逻辑, 直接cv登录页的逻辑
    const requestLogin = useCallback(
        async (credential: string) => {
            const data = {
                credential,
                scene: 'ECOFLOW_WEB',
                returnTo: HOME_HOST,
                returnToNew: linkHandle(HOME_HOST),
                shopifyDomain: `https://${router.locale}.ecoflow.com`,
                logInSource: 1,
            };

            const res = await request.post(`${loginUrl(router.locale)}/auth/website/oauth/oneTapSignInByGoogle`, {
                needToken: false,
                data,
            });

            if (res.code === '0') {
                onLogin(res.data, false);

                if (res.data?.user?.destroyed === 'DESTROY') {
                    window.location.href = `/${router.locale || ''}/my-account-manage`;
                    return;
                }

                gtm.push({
                    event: 'uaEvent',
                    event_name: 'login',
                    page_name: '/login',
                });

                try {
                    const res2 = await syncLoginShopify();
                    window.location.href = String(res2);
                } catch (err) {
                    window.location.href = `/${router.locale || ''}`;
                }
            } else {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'login_fail',
                    page_name: '/login',
                });
            }
        },
        [HOME_HOST, linkHandle, router.locale, syncLoginShopify],
    );

    const loadGoogleScript = useCallback(async (): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (window.google?.accounts?.id) {
                resolve();
                return;
            }

            const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
            if (existingScript) {
                existingScript.addEventListener('load', () => resolve());
                existingScript.addEventListener('error', () => reject(new Error('Failed to load Google script')));
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;

            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Google script'));

            document.head.appendChild(script);
        });
    }, []);

    const fetchClientId = useCallback(async (): Promise<string> => {
        const res = await request.get(`${loginUrl(router.locale)}/auth/website/oauth/oAuthClientId/GOOGLE`, {
            needToken: false,
        });
        if (res.code === '0' && res.data) {
            return res.data;
        }
        throw new Error(res.message || 'Failed to fetch client ID');
    }, [router.locale]);

    const initializeGoogleLogin = useCallback(async () => {
        if (initializationRef.current || !shouldShowComponent()) {
            return;
        }

        initializationRef.current = true;

        try {
            const [, clientId] = await Promise.all([loadGoogleScript(), fetchClientId()]);

            (window.google as any).accounts.id.initialize({
                client_id: clientId,
                callback: (res: any) => {
                    const credential = res.credential;
                    if (credential) {
                        requestLogin(credential);
                    }

                    gtm.push({
                        event: 'uaEvent',
                        event_name: 'google_win_login_success',
                        __bth_only_ef: true,
                    });
                    // onSuccess?.(credential);
                },
            });

            (window.google as any).accounts.id.prompt();
        } catch (error) {
            console.error('Failed to initialize Google login:', error);
            // onError?.();
        }
    }, [shouldShowComponent, loadGoogleScript, fetchClientId, requestLogin]);

    useEffect(() => {
        initializeGoogleLogin();
    }, [initializeGoogleLogin]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsLogin(getIsLogin());
        }
    }, []);

    if (!shouldShowComponent()) {
        return null;
    }

    return <></>;
}
